import React, { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { useCreateUserSupplierMutation } from "store/supplier/supplier.api";
import { useSelector } from "react-redux";
import { useGetValidateursByCompanyQuery } from "store/user/userApi";
import { useCreateUserMutation } from "store/user/userApi";
import { useGetRolesByCompanyQuery } from "store/role/role.api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { supplierUser } from "locales/fr/supplierUser";

const CreateSupplierUserModal = ({
  show,
  handleClose,
  supplierId,
  companyId,
  onUserCreated,
}) => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);

  const [newUserData, setNewUserData] = useState({
    email: "",
    name: "",
    validateur: "",
    companyId: companyId || "",
  });

  const [creatingNewValidateur, setCreatingNewValidateur] = useState(false);
  const [newValidateurData, setNewValidateurData] = useState({
    name: "",
    email: "",
    roleId: "",
    companyId: companyId || currentUser.companyId,
  });

  const [createUser] = useCreateUserMutation();
  const [createUserSupplier, { isLoading }] = useCreateUserSupplierMutation();

  const { data: validateurs = [], isLoading: isLoadingValidateurs } =
    useGetValidateursByCompanyQuery(companyId || currentUser.companyId);

  const { data: rolesData } = useGetRolesByCompanyQuery(
    companyId || currentUser.companyId
  );
  const validateurRoles =
    rolesData?.data?.filter((r) => r.permissions.includes("VALIDATEUR")) || [];

  useEffect(() => {
    setNewUserData({
      email: "",
      name: "",
      validateur: "",
      companyId: companyId || currentUser.companyId,
    });
    setNewValidateurData({
      name: "",
      email: "",
      roleId: "",
      companyId: companyId || currentUser.companyId,
    });
  }, [show, companyId, currentUser.companyId]);

  const handleCreateValidateur = async () => {
    if (
      !newValidateurData.name ||
      !newValidateurData.email ||
      !newValidateurData.roleId
    )
      return toast.error(t("supplierUser.form.fill_all_fields_validator"));
    try {
      const result = await createUser(newValidateurData).unwrap();
      setNewUserData({ ...newUserData, validateur: result._id });
      setCreatingNewValidateur(false);
      toast.success(t("supplierUser.form.validator_created_success"));
    } catch (err) {
      console.error(err);
      toast.error(t("supplierUser.form.error_create_validator"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newUserData.validateur) {
      alert(t("supplierUser.form.select_or_create_validator"));
      return;
    }
    try {
      await createUserSupplier({ supplierId, userData: newUserData }).unwrap();
      alert(t("supplierUser.form.user_created_success"));
      handleClose();
      if (onUserCreated) onUserCreated();
    } catch (error) {
      console.error(error);
      alert(t("supplierUser.form.error_create_user"));
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="modal-sign">
      <Modal.Header closeButton>
        <Modal.Title>{t("supplierUser.modal_title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {/* Nom & Email utilisateur fournisseur */}
          <Form.Group className="mb-3">
            <Form.Label>{t("supplierUser.form.name")}</Form.Label>
            <Form.Control
              type="text"
              value={newUserData.name}
              onChange={(e) =>
                setNewUserData({ ...newUserData, name: e.target.value })
              }
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("supplierUser.form.email")}</Form.Label>
            <Form.Control
              type="email"
              value={newUserData.email}
              onChange={(e) =>
                setNewUserData({ ...newUserData, email: e.target.value })
              }
              required
            />
          </Form.Group>

          {/* Select ou création du validateur */}
          {!creatingNewValidateur ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{t("supplierUser.form.validator")}</Form.Label>
                <Form.Select
                  value={newUserData.validateur}
                  onChange={(e) =>
                    setNewUserData({
                      ...newUserData,
                      validateur: e.target.value,
                    })
                  }
                  required
                  disabled={isLoadingValidateurs}>
                  <option value="">
                    {t("supplierUser.form.select_validator")}
                  </option>
                  {validateurs?.data?.map((v) => (
                    <option key={v._id} value={v._id}>
                      {v.name} ({v.email})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button
                variant="link"
                onClick={() => setCreatingNewValidateur(true)}>
                {t("supplierUser.form.create_new_validator")}
              </Button>
            </>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{t("supplierUser.form.validator_name")}</Form.Label>
                <Form.Control
                  type="text"
                  value={newValidateurData.name}
                  onChange={(e) =>
                    setNewValidateurData({
                      ...newValidateurData,
                      name: e.target.value,
                    })
                  }
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  {t("supplierUser.form.validator_email")}
                </Form.Label>
                <Form.Control
                  type="email"
                  value={newValidateurData.email}
                  onChange={(e) =>
                    setNewValidateurData({
                      ...newValidateurData,
                      email: e.target.value,
                    })
                  }
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t("supplierUser.form.validator_role")}</Form.Label>
                <Form.Select
                  value={newValidateurData.roleId}
                  onChange={(e) =>
                    setNewValidateurData({
                      ...newValidateurData,
                      roleId: e.target.value,
                    })
                  }
                  required>
                  <option value="">{t("supplierUser.form.select_role")}</option>
                  {validateurRoles.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Button
                variant="secondary"
                onClick={() => setCreatingNewValidateur(false)}
                className="me-2">
                {t("supplierUser.form.cancel")}
              </Button>
              <Button variant="primary" onClick={handleCreateValidateur}>
                {t("supplierUser.form.create_validator")}
              </Button>
            </>
          )}

          <Button
            type="submit"
            className="btn-sign w-100 mt-3"
            disabled={isLoading}>
            {isLoading
              ? t("supplierUser.form.creating")
              : t("supplierUser.form.create_user")}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateSupplierUserModal;
